import axios from "./_axios";
import { API } from "./apiEndPoints";

/**
 * Account Adoption read (v2 only) — ER-12305 endpoint.
 * Returns the latest nightly snapshot for the given period window.
 *
 * @param {string|number} accountId
 * @param {30|90|365} [period=30]
 */
export const getAccountAdoption = (accountId, period = 30) => {
  return axios()({
    method: "get",
    url: `${API.ACCOUNT_ADOPTION}${accountId}/adoption/`,
    params: { period },
  });
};

/**
 * Defensive normalizer: the backend (ER-12305) owns the exact field names; this keeps
 * the UI resilient to a missing optional block while the contract stabilises.
 */
export function mapAdoptionPayload(payload) {
  const p = payload || {};
  const hasData = p.has_data !== false;

  const metrics = p.metrics || {};
  const formula = p.formula || {};

  return {
    has_data: hasData,
    snapshot_date: p.snapshot_date ?? null,
    config_version: p.config_version ?? null,
    score: p.score ?? 0,
    band: normalizeBand(p.band),
    trend: p.trend ?? 0,
    formula: {
      weights: formula.weights || {},
      bands: formula.bands || {},
      bands_cutoffs: formula.band_cutoffs || formula.bands || {},
    },
    metrics: {
      breadth: metricOf(metrics.breadth, "Breadth · active people"),
      depth: metricOf(metrics.depth, "Depth · actions completed"),
      value: metricOf(metrics.value, "Value · outcomes"),
      momentum: metricOf(metrics.momentum, "Momentum · vs 90-day avg"),
    },
    products: (p.products || []).map(mapProduct),
    roles: (p.roles || []).map(mapRole),
    weekly_series: mapWeeklySeries(p.weekly_series),
    signals: (p.signals || []).map(mapSignal),
  };
}

function normalizeBand(band) {
  const raw = String(band || "stalling").toLowerCase();
  if (raw === "sticky") return "sticky";
  if (raw === "watch") return "watch";
  return "stalling";
}

function metricOf(raw, fallbackLabel) {
  const m = raw || {};
  return {
    label: m.label || fallbackLabel,
    value: m.value ?? m.display ?? "—",
    delta: m.delta ?? m.change ?? null,
    delta_direction:
      m.delta_direction ?? m.change_direction ?? deltaDirectionOf(m.delta ?? m.change),
  };
}

function deltaDirectionOf(delta) {
  if (delta == null) return null;
  const n = Number(delta);
  if (Number.isNaN(n)) return null;
  if (n > 0) return "up";
  if (n < 0) return "down";
  return "flat";
}

function mapProduct(raw) {
  const p = raw || {};
  return {
    key: p.key ?? p.product ?? null,
    name: p.name ?? p.product ?? "—",
    always_on: !!p.always_on,
    status: normalizeBand(p.status ?? p.band),
    who_uses_it: p.who_uses_it ?? "—",
    key_actions: p.key_actions ?? p.key_actions_last_30_days ?? "—",
    result: mapResult(p.result),
  };
}

function mapResult(raw) {
  const r = raw || {};
  return {
    type: r.type || (r.value != null && r.label ? "progress" : "text"),
    label: r.label ?? r.display ?? null,
    value: r.value ?? r.progress ?? null,
    trend: r.trend ?? r.change ?? null,
    trend_direction:
      r.trend_direction ?? r.change_direction ?? deltaDirectionOf(r.trend ?? r.change),
  };
}

function mapRole(raw) {
  const r = raw || {};
  return {
    role: r.role ?? r.name ?? "—",
    active: r.active ?? r.n ?? 0,
    total: r.total ?? r.m ?? 0,
    hint: r.hint ?? r.hint_line ?? null,
  };
}

function mapWeeklySeries(raw) {
  const w = raw || {};
  const series = w.series || {};
  return {
    weeks: Array.isArray(w.weeks) ? w.weeks : [],
    series: {
      tasks_completed: asArray(series.tasks_completed ?? series.tasksCompleted),
      checkpoints_inspections: asArray(
        series.checkpoints_inspections ?? series.checkpointsInspections
      ),
      pay_plan_proposals: asArray(series.pay_plan_proposals ?? series.payPlanProposals),
    },
  };
}

function mapSignal(raw) {
  const s = raw || {};
  return {
    group: s.group ?? s.product_group ?? "Account-wide (people)",
    product_status_badge: s.product_status_badge ?? s.status_badge ?? null,
    severity: normalizeSeverity(s.severity),
    title: s.title ?? "—",
    description: s.description ?? s.why_it_matters ?? "",
    do_next: s.do_next ?? s.doNext ?? "",
    playbook_url: s.playbook_url ?? s.playbookUrl ?? "",
  };
}

function normalizeSeverity(severity) {
  const raw = String(severity || "warning").toLowerCase();
  if (raw === "critical" || raw === "error") return "critical";
  if (raw === "positive" || raw === "success" || raw === "good") return "positive";
  return "warning";
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}
